import React, { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { keyframes } from "@emotion/react";

import { ColorMode, Flex, IconButton, Wrapper } from "components";

const fadeIn = keyframes({ from: { opacity: 0 }, to: { opacity: 1 } });
const fadeOut = keyframes({ from: { opacity: 1 }, to: { opacity: 0 } });

const overlayStyles = (theme) => ({
  position: "fixed",
  inset: 0,
  zIndex: 9998,
  background: theme.colors.fill[0],
  '&[data-state="open"]': { animation: `${fadeIn} 200ms ease-out` },
  '&[data-state="closed"]': { animation: `${fadeOut} 200ms ease-out` },
});

const contentStyles = (theme) => ({
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  background: theme.colors.brand.primary,
  boxShadow: `
    0 2.8px 2.2px rgba(0, 0, 0, 0.02),
    0 6.7px 5.3px rgba(0, 0, 0, 0.028),
    0 12.5px 10px rgba(0, 0, 0, 0.035),
    0 22.3px 17.9px rgba(0, 0, 0, 0.042),
    0 41.8px 33.4px rgba(0, 0, 0, 0.05),
    0 100px 80px rgba(0, 0, 0, 0.07)
  `,
  "&:focus": { outline: 0 },
  '&[data-state="open"]': { animation: `${fadeIn} 200ms ease-out` },
  '&[data-state="closed"]': { animation: `${fadeOut} 200ms ease-out` },
});

// Visually-hidden accessible title (Radix Dialog requires a title).
const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

function Panel({ children, panel, panelState, setPanelState, ...rest }) {
  const [open, setOpen] = useState(false);

  // Optional external control of the panel state.
  useEffect(() => {
    if (panelState !== undefined) setOpen(panelState);
  }, [panelState]);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (setPanelState) setPanelState(next);
  };

  const close = () => handleOpenChange(false);

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger data-component="Panel.Button" {...rest}>
        {children}
      </Dialog.Trigger>
      <Dialog.Portal>
        <ColorMode mode="dark">
          <Dialog.Overlay data-component="Panel.Overlay" css={overlayStyles} />
          <Dialog.Content data-component="Panel.Content" css={contentStyles}>
            <Dialog.Title css={visuallyHidden}>Menu</Dialog.Title>
            <Flex
              data-component="Panel.Inner"
              flexDirection="column"
              flex={1}
              pr={{ _: 0, md: "calc(((100vw - 1400px) / 2) - 32px)" }}
            >
              <Wrapper>
                <Flex justifyContent="flex-start" mt="layout.5">
                  <IconButton
                    symbol={"close-line"}
                    intent="default"
                    appearance="minimal"
                    round
                    onClick={close}
                  />
                </Flex>
              </Wrapper>
              {React.cloneElement(panel.children, {
                showPanel: open,
                open: () => handleOpenChange(true),
                close: close,
              })}
            </Flex>
          </Dialog.Content>
        </ColorMode>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default Panel;
